import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, ArrowRight } from 'lucide-react';
interface OrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function OrderFormModal({
  isOpen,
  onClose
}: OrderFormModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    // Reset after animation completes
    setTimeout(() => {
      setFirstName('');
      setLastName('');
      setEventDate('');
      setSubmitted(false);
    }, 300);
  };
  return <AnimatePresence data-id="element-250">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-id="element-251">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} data-id="element-252" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        duration: 0.4,
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="relative w-full max-w-md bg-brown-700 rounded-3xl shadow-2xl overflow-hidden border border-white/10" data-id="element-253">
            {/* Gold accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-accent-purple to-accent-pink" data-id="element-254" />

            {/* Close button */}
            <button onClick={handleClose} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-cream-400 hover:text-white" data-id="element-255">
              <X size={16} data-id="element-256" />
            </button>

            <div className="p-8 md:p-10" data-id="element-257">
              {!submitted ? <>
                  {/* Header */}
                  <div className="mb-8" data-id="element-258">
                    <h2 className="text-3xl font-serif italic text-white mb-2" data-id="element-259">
                      Try It Free
                    </h2>
                    <p className="text-cream-400 text-sm leading-relaxed" data-id="element-260">
                      Fill in your details and we'll set up your audio guestbook
                      demo in minutes.
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-261">
                    <div className="grid grid-cols-2 gap-4" data-id="element-262">
                      <div data-id="element-263">
                        <label className="block text-xs font-bold text-cream-400 uppercase tracking-wider mb-2" data-id="element-264">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required placeholder="Sarah" className="w-full bg-brown-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-cream-500 focus:outline-none focus:border-accent-pink focus:ring-1 focus:ring-accent-pink/50 transition-all text-sm" data-id="element-265" />
                      </div>
                      <div data-id="element-266">
                        <label className="block text-xs font-bold text-cream-400 uppercase tracking-wider mb-2" data-id="element-267">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required placeholder="Johnson" className="w-full bg-brown-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-cream-500 focus:outline-none focus:border-accent-pink focus:ring-1 focus:ring-accent-pink/50 transition-all text-sm" data-id="element-268" />
                      </div>
                    </div>

                    <div data-id="element-269">
                      <label className="block text-xs font-bold text-cream-400 uppercase tracking-wider mb-2" data-id="element-270">
                        Event Date
                      </label>
                      <div className="relative" data-id="element-271">
                        <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} required className="w-full bg-brown-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-accent-pink focus:ring-1 focus:ring-accent-pink/50 transition-all text-sm appearance-none [color-scheme:dark]" data-id="element-272" />
                      </div>
                    </div>

                    <button type="submit" className="w-full bg-gradient-to-r from-accent-purple to-accent-pink text-white text-sm font-bold tracking-widest uppercase px-6 py-4 rounded-full hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-2 group mt-2" data-id="element-273">
                      <span data-id="element-274">Get Started</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-275" />
                    </button>
                  </form>

                  <p className="text-center text-xs text-cream-500 mt-5" data-id="element-276">
                    Free to try · No credit card required
                  </p>
                </> /* Success State */ : <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-center py-8" data-id="element-277">
                  <div className="w-16 h-16 rounded-full bg-accent-purple/20 flex items-center justify-center mx-auto mb-6" data-id="element-278">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: 'spring',
                stiffness: 200
              }} className="text-accent-purple text-3xl" data-id="element-279">
                      ✓
                    </motion.div>
                  </div>
                  <h3 className="text-2xl font-serif italic text-white mb-3" data-id="element-280">
                    You're All Set!
                  </h3>
                  <p className="text-cream-400 text-sm leading-relaxed mb-6" data-id="element-281">
                    Thanks, {firstName}! We'll have your demo guestbook ready
                    shortly. Check your email for next steps.
                  </p>
                  <button onClick={handleClose} className="text-accent-pink text-sm font-bold uppercase tracking-widest hover:text-white transition-colors" data-id="element-282">
                    Close
                  </button>
                </motion.div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}